import { useEffect, useRef, useState } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import styles from './EditorExtension_ImageList.module.css';
import { useCCContext } from '../CC/CCContext';
import { CC, ccConfig } from '../CC/CC';
import * as ccUtils from '../CC/utils';
import { generateQuery, replaceBasketIdSubqueries, postWithCache } from '../CC/utils';
import { getImages } from '../Result/getImages';
import { Gallery } from 'react-grid-gallery';
import PhotoAlbum from "react-photo-album";
import "react-photo-album/styles.css"

const ReactGridGalleryImageList = ({ records, state, props, imageHeight, margin, layout, spacing, padding, rowHeight, columns, numImages, compClass }) => {
  const { setCCContext, clearSearch, setViewMode, recordsPerPage } = useCCContext();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true); // Added loading state
  const defaultHeight = 500

  useEffect(() => {
    const loadImages = async () => {
      const imagePromises = records.map(async (record, index) => {
        const allImages = getImages(record);
        //console.log(allImages);
        const imageUrl = allImages["imagebare"]?.[0]?.url + '&bg=ffffff&height=' + defaultHeight || `${process.env.REACT_APP_IMAGE_BASE_URL}nondisponible.cci&height=${defaultHeight}&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=30&passepartoutheight=0&passepartoutcolor=ffffff&cache=yes`;

        try {
          const img = new Image();
          img.src = imageUrl;
          await img.decode();
          return {
            src: imageUrl,
            width: img.width,
            height: img.height,
            title: JSON.stringify({title: record?.data?.record?.carteltitle, loc: record?.data?.record?.cartellocdescr, creator: record?.data?.record?.cartelcreator, dated: record?.data?.record?.carteldated}),
            index: index,
            alt: record?.data?.record?.title?.title //`Image ${index + 1}`,
          };
        } catch (error) {
          return {
            src: `${process.env.REACT_APP_IMAGE_BASE_URL}nondisponible.cci&height=${defaultHeight}&width=${defaultHeight}&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=30&passepartoutheight=0&passepartoutcolor=ffffff&cache=yes`,
            width: defaultHeight,
            height: defaultHeight,
            title: JSON.stringify({title: record?.data?.record?.carteltitle, loc: record?.data?.record?.cartellocdescr, creator: record?.data?.record?.cartelcreator, dated: record?.data?.record?.carteldated}),
            index: index,
            alt: record?.data?.record?.title?.title, //`Image ${index + 1}`,
          };
        }
      });

      const loadedImages = await Promise.all(imagePromises);
      setImages(loadedImages);
      setLoading(false); // Set loading to false after images are loaded
    };

    loadImages();
  }, [records, imageHeight, margin, layout, spacing, padding, rowHeight, columns, numImages, compClass]);

  const handleClick = async (index) => {
    if (!props.editor.isEditable) {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('albumexternal')) {
        window.open(`${window.location.origin}${window.location.pathname}?state=${state}&index=${index}`, '_blank');
      } else {

        if (state) {
          try {
            //const defaultCCObj = new CC();
            let startUPCCObj = state ? await ccUtils.GetCCFromState(state.replace(/'/g, '"')) : new CC();
            //const startUPCCObj = ccUtils.GetCCFromState(state) //.deepMerge(defaultCCObj, JSON.parse(state));
            setCCContext({
              cc: startUPCCObj,
              first: Math.floor((index - 1) / recordsPerPage) * recordsPerPage + 1,
              selectedRecord: index,
              viewMode: 'list',
              sort: startUPCCObj.sort
            });
          } catch (error) {
            console.error("Error parsing state:", error);
          }
        } else {
          clearSearch();
          setViewMode('list');
        }
      }
    }
  };

  // Map images to include the onClick handler
  const galleryImages = images.map((image) => ({
    ...image,
    customOverlay: image.customOverlay,
    // Optionally, you can include custom properties if needed
  }));

  return (
    <div className={styles.galleryContainer}>
      {loading ? (
        <div className={styles.placeholderContainer}>
          {Array.from({ length: numImages }).map((_, idx) => (
            <div
              key={idx}
              className={styles.placeholder}
            >
              <div className={styles.loadingSpinner}></div>
            </div>
          ))}
        </div>
      ) : (
       /* <Gallery
          images={galleryImages}
          enableImageSelection={false}
          onClick={(index) => handleClick(galleryImages[index].index + 1)}
          margin={margin}
          rowHeight={imageHeight}
          customStyles={{
            // Add any custom styles if necessary
          }}
        />*/
        <PhotoAlbum
          photos={galleryImages}
          layout={layout}
          columns={columns}
          targetRowHeight={rowHeight}
          spacing={spacing}
          padding={padding}
          onClick={(index) => handleClick(galleryImages[index.index].index + 1)}
          render={{
            image: (props) => {
              const cartel = JSON.parse(props.title)
              return (
              <div class={styles.recordContainer}>
                <div class={`${styles.recordOverlay} ${compClass}`}>
                  <div class={'carteltitle'}>
                    {cartel.title}
                  </div>
                  <div class={'cartelother'}>
                    {cartel.creator}<br />{cartel.dated}<br />{cartel.loc}
                  </div>
                </div>
                <img {...props} />
              </div>)
            }
          }}
        />
      )}
    </div>
  );
};

export default Node.create({
  name: 'reactComponentImageList',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      selected: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-selected') === 'true',
        renderHTML: (attributes) => ({
          'data-selected': attributes.selected
        }),
      },
      state: {
        default: '',
        parseHTML: (element) => {
          const state = element.getAttribute('data-state');
          return state;
        },
        renderHTML: (attributes) => ({
          'data-state': attributes.state
        }),
      },
      compClass: {
        default: '',
        parseHTML: (element) => {
          const state = element.getAttribute('data-class');
          return state;
        },
        renderHTML: (attributes) => ({
          'data-class': attributes.compClass
        }),
      },
      imageHeight: {
        default: 100,
        parseHTML: (element) => parseInt(element.getAttribute('data-image-height') || '100', 10),
        renderHTML: (attributes) => ({
          'data-image-height': attributes.imageHeight
        }),
      },
      margin: {
        default: 5,
        parseHTML: (element) => parseInt(element.getAttribute('data-margin') || '5', 10),
        renderHTML: (attributes) => ({
          'data-margin': attributes.margin
        }),
      },
      numImages: {
        default: 100,
        parseHTML: (element) => parseInt(element.getAttribute('data-num-images') || '100', 10),
        renderHTML: (attributes) => ({
          'data-num-images': attributes.numImages
        }),
      },
      layout: {
        default: 'columns',
        parseHTML: (element) => element.getAttribute('data-layout') || 'columns',
        renderHTML: (attributes) => ({
          'data-layout': attributes.layout
        }),
      },
      spacing: {
        default: 0,
        parseHTML: (element) => parseInt(element.getAttribute('data-spacing') || '0', 10),
        renderHTML: (attributes) => ({
          'data-spacing': attributes.spacing
        }),
      },
      padding: {
        default: 0,
        parseHTML: (element) => parseInt(element.getAttribute('data-padding') || '0', 10),
        renderHTML: (attributes) => ({
          'data-padding': attributes.padding
        }),
      },
      columns: {
        default: 3,
        parseHTML: (element) => parseInt(element.getAttribute('data-columns') || '3', 10),
        renderHTML: (attributes) => ({
          'data-columns': attributes.columns
        }),
      },
      rowHeight: {
        default: 150,
        parseHTML: (element) => parseInt(element.getAttribute('data-row-height') || '150', 10),
        renderHTML: (attributes) => ({
          'data-row-height': attributes.rowHeight
        }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'ImageList',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['ImageList', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props) => {
  const { selected, updateAttributes } = props;
  const { state, imageHeight, margin, padding, spacing, layout, rowHeight, columns, numImages, compClass } = props.node.attrs;
  const [records, setRecords] = useState([]);
  const componentRef = useRef({});
  const { user } = useCCContext();

  //const { setCCContext, clearSearch, setViewMode, recordsPerPage } = useCCContext();

  useEffect(() => {
    const fetchStateImageList = async () => {
      try {
/*        let stateCC = {};
        if (state && state !== '') {
          stateCC = JSON.parse(state);
        } else {
          stateCC = new CC();
        }*/
        let stateCC = state ? await ccUtils.GetCCFromState(state.replace(/'/g, '"')) : new CC();
        let query = generateQuery(stateCC, user);
        query = await replaceBasketIdSubqueries(query);
        const payload = {
          action: "get",
          command: "search",
          query: query,
          range: `1-${props.node.attrs.numImages}`,
          responseformat: "json",
          sort: ccConfig.sort[stateCC.sort].field
        };
        const response = await postWithCache(process.env.REACT_APP_CC_URL, payload, componentRef.current);
        setRecords(response.records.record);
      } catch (error) {
        setRecords([]);
        console.error("Error fetching state imageList:", error);
      }
    };

    fetchStateImageList();
  }, [state, imageHeight, margin, padding, spacing, layout, rowHeight, columns, numImages, compClass, user]);

  useEffect(() => {
    const handleSelection = () => {
      updateAttributes({ selected: props.editor.isActive('reactComponentImageList') });
    };

    props.editor.on('selectionUpdate', handleSelection);

    return () => {
      props.editor.off('selectionUpdate', handleSelection);
    };
  }, [props.editor, updateAttributes]);

  const handleDoubleClick = (event) => {
    if (props.editor.isEditable) {
      event.stopPropagation();
      const { state, query, htmlStyle, imageHeight, margin, padding, spacing, layout, rowHeight, columns, numImages, compClass } = props.node.attrs;
      const customEvent = new CustomEvent('showInsertContentPopup', {
        detail: {
          extension: 'ImageList',
          props: { state, query, htmlStyle, imageHeight, margin, padding, spacing, layout, rowHeight, columns, numImages, compClass },
          pos: props.getPos(),
          node: props.node
        },
        bubbles: true,
        cancelable: true
      });
      event.target.dispatchEvent(customEvent);
    }
  };

  return (
    <NodeViewWrapper as="span" className={`react-component ${selected ? 'selected' : ''}`}>
      <div className={styles.hyperlink} onDoubleClick={handleDoubleClick}>
        <ReactGridGalleryImageList records={records} state={state} props={props} imageHeight={imageHeight} margin={margin} padding={padding} spacing={spacing} layout={layout} rowHeight={rowHeight} columns={columns} numImages={numImages} compClass={compClass} />
      </div>
    </NodeViewWrapper>
  );
};
